import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';

import Header from './components/Header';
import Hero from './components/Hero';
import Stats from './components/Stats';
import Features from './components/Features';
import ProductShowcase from './components/ProductShowcase';
import InteractiveDiagram from './components/InteractiveDiagram';
import FAQ from './components/FAQ';
import Blog from './components/Blog';
import MarqueeBanner from './components/MarqueeBanner';
import ClubSelector from './components/ClubSelector';
import Footer from './components/Footer';
import AboutPartner from './components/AboutPartner';
import QuickAnswers from './components/QuickAnswers';
import ColorStraps from './components/ColorStraps';
import VideoShowcase from './components/VideoShowcase';
import PromoBanner from './components/PromoBanner';
import ResearchSnippet from './components/ResearchSnippet';

import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import FAQPage from './pages/FAQPage';
import BilateralMasteryPage from './pages/BilateralMasteryPage';
const AdminConsole = React.lazy(() => import('./pages/AdminConsole'));
import ContactPage from './pages/ContactPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AboutPage from './pages/AboutPage';
import TermsOfService from './pages/TermsOfService';
import NotFoundPage from './pages/NotFoundPage';
import WhatsAppButton from './components/WhatsAppButton';
import ChatWidget from './components/ChatWidget';
import FloatingBuyButton from './components/FloatingBuyButton';
import CookieBanner from './components/CookieBanner';
import GoogleStoreFloatingBadge from './components/GoogleStoreFloatingBadge';
import GoogleMerchantModal from './components/GoogleMerchantModal';

import { articles as initialArticles } from './data/articles';
import { faqs as initialFaqs } from './data/faqs';
import { db } from './firebase';
import { collection, onSnapshot, query } from 'firebase/firestore';

// ── Per-route SEO: update <title> and <meta name="description"> ──────────────
const PAGE_META = {
  '/': {
    title: 'Playermaker 2.0 Canada | iLOVEthisGAME | Free Shipping CAD $279 ($249 with Club Code)',
    description: "Official Canadian partner for Playermaker 2.0 smart soccer tracker. Regular CAD $279, or $249 with club code. Free shipping in Canada, no duty fees. FIFA-approved boot sensor.",
  },
  '/home': {
    title: 'Playermaker 2.0 Canada | iLOVEthisGAME | Free Shipping CAD $279 ($249 with Club Code)',
    description: "Official Canadian partner for Playermaker 2.0 smart soccer tracker. Regular CAD $279, or $249 with club code. Free shipping in Canada, no duty fees. FIFA-approved boot sensor.",
  },
  '/bilateral-mastery': {
    title: 'Bilateral Mastery Research Paper | Playermaker Pro | iLOVEthisgame.ca',
    description: 'Closing the Gap: Bilateral Football Mastery in the Modern Game. Research data on 30,000+ youth soccer players, 16.8% kicking asymmetry, and 360-degree passing arcs.',
  },
  '/about': {
    title: 'About Us | Playermaker Canada | iLOVEthisgame.ca',
    description: 'Learn about iLOVEthisgame.ca, 100% Canadian owned official partner of Playermaker smart soccer wearable tech. Free shipping in Canada, no duty fees.',
  },
  '/faq': {
    title: 'FAQ & Support | Playermaker Canada | iLOVEthisgame.ca',
    description: 'Get answers about Playermaker 2.0: setup, charging, straps, free Canadian shipping, 1-year warranty, and local CAD support.',
  },
  '/blog': {
    title: 'Soccer Performance Blog | Playermaker Canada | iLOVEthisgame.ca',
    description: 'Insights on soccer analytics, wearable tech, Canadian player development, and training science from iLOVEthisgame.ca.',
  },
  '/contact': {
    title: 'Contact Us | Playermaker Canada | iLOVEthisgame.ca',
    description: 'Get in touch with iLOVEthisgame.ca team for Playermaker 2.0 inquiries, support, academy sales, and warranty assistance.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | iLOVEthisgame.ca',
    description: 'Privacy policy for iLOVEthisgame.ca: how we collect, use, and protect your data in compliance with PIPEDA.',
  },
  '/terms-of-service': {
    title: 'Terms of Service | iLOVEthisgame.ca',
    description: 'Terms of Service for iLOVEthisgame.ca: terms and conditions for using our website and purchasing Playermaker products.',
  },
};

function useSEO(articles) {
  const { pathname } = useLocation();

  useEffect(() => {
    let meta = PAGE_META[pathname];

    // Handle dynamic blog post routes: /blog/:id
    if (!meta && pathname.startsWith('/blog/')) {
      const slug = pathname.replace('/blog/', '');
      const article = articles?.find(a => a.id === slug);
      if (article) {
        meta = {
          title: `${article.title} | iLOVEthisgame.ca`,
          description: article.excerpt || `Read "${article.title}" on the iLOVEthisgame.ca performance blog.`,
        };
      }
    }

    if (meta) {
      document.title = meta.title;
      let descTag = document.querySelector('meta[name="description"]');
      if (descTag) descTag.setAttribute('content', meta.description);
    }

    // Dynamic Canonical URL update to resolve canonicalization warnings
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      const canonicalUrl = `https://ilovethisgame.ca${pathname === '/' ? '/' : pathname}`;
      canonicalTag.setAttribute('href', canonicalUrl);
    }
  }, [pathname, articles]);
}

// Scroll to top helper when switching routes
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id) || document.querySelector(hash);
        if (element) {
          const promoEl = document.querySelector('.promo-banner, .announcement-bar');
          const promoH = promoEl ? Math.ceil(promoEl.getBoundingClientRect().height) : 0;
          const offset = promoH + 80 + 20;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementPosition - offset,
            behavior: 'smooth'
          });
          return;
        }
      }, 50);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

// Home Page Component (Previews top articles & top FAQs)
function HomePage({ onNavigate, articles, faqs }) {
  return (
    <>
      <Hero />
      {/* Stacked middle video with text overlay right under Hero */}
      <VideoShowcase 
        src="https://cdn.shopify.com/videos/c/o/v/0d8845e6bf9c4be8af8162d1263b7a8e.mp4" 
        showText={true}
      />
      <MarqueeBanner />
      <div id="how-it-works">
        <Stats />
      </div>
      <AboutPartner />
      <Features />
      <ResearchSnippet onNavigate={onNavigate} />
      <InteractiveDiagram />
      <ProductShowcase />
      <ColorStraps />
      <ClubSelector />
      {/* Bottom video with no text written on top */}
      <VideoShowcase 
        src="/playermaker_video.mp4" 
        showText={false}
      />
      <QuickAnswers />
      <FAQ faqs={faqs} onNavigate={onNavigate} />
      <Blog articles={articles} onNavigate={onNavigate} />
    </>
  );
}

// Wrapper for BlogPostPage to extract URL params
function BlogPostPageWrapper({ articles, onNavigate }) {
  const { id } = useParams();
  return <BlogPostPage key={id} articleId={id} articles={articles} onNavigate={onNavigate} />;
}

// Main App Container
function AppContent() {
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    // Always default to dark for first-time visitors
    const stored = localStorage.getItem('iltg-theme');
    if (!stored) {
      localStorage.setItem('iltg-theme', 'dark');
      return 'dark';
    }
    return stored;
  });

  const sortedInitialArticles = [...initialArticles].sort((a, b) => new Date(b.date) - new Date(a.date));
  const [articles, setArticles] = useState(sortedInitialArticles);
  const [faqs, setFaqs] = useState(initialFaqs);

  // Fetch live data from Firestore in background without blocking initial page render
  useEffect(() => {
    let unsubArticles = () => {};
    let unsubFaqs = () => {};

    try {
      const qArticles = query(collection(db, 'articles'));
      unsubArticles = onSnapshot(
        qArticles,
        (snapshot) => {
          const arts = [];
          snapshot.forEach(doc => arts.push({ id: doc.id, ...doc.data() }));
          if (arts.length > 0) {
            arts.sort((a, b) => new Date(b.date) - new Date(a.date));
            setArticles(arts);
          }
        },
        (error) => {
          console.warn('Firestore articles listener warning:', error);
        }
      );

      const qFaqs = query(collection(db, 'faqs'));
      unsubFaqs = onSnapshot(
        qFaqs,
        (snapshot) => {
          const fqs = [];
          snapshot.forEach(doc => fqs.push({ id: doc.id, ...doc.data() }));
          if (fqs.length > 0) {
            setFaqs(fqs);
          }
        },
        (error) => {
          console.warn('Firestore faqs listener warning:', error);
        }
      );
    } catch (e) {
      console.warn('Firestore listener catch:', e);
    }

    return () => {
      if (typeof unsubArticles === 'function') unsubArticles();
      if (typeof unsubFaqs === 'function') unsubFaqs();
    };
  }, []);

  // Dynamic per-page SEO
  useSEO(articles);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (currentTheme) => {
      if (currentTheme === 'light') {
        root.classList.add('light-theme');
      } else if (currentTheme === 'dark') {
        root.classList.remove('light-theme');
      } else if (currentTheme === 'system') {
        const isSystemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (isSystemDark) {
          root.classList.remove('light-theme');
        } else {
          root.classList.add('light-theme');
        }
      }
    };

    applyTheme(theme);
    localStorage.setItem('iltg-theme', theme);

    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleSystemThemeChange = (e) => {
        if (e.matches) {
          root.classList.remove('light-theme');
        } else {
          root.classList.add('light-theme');
        }
      };

      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }
  }, [theme]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  // Track promo banner height to push the fixed header down correctly
  useEffect(() => {
    const updateHeights = () => {
      try {
        const promoEl = document.querySelector('.promo-banner, .announcement-bar');
        const promoH = promoEl ? Math.ceil(promoEl.getBoundingClientRect().height) : 0;
        document.documentElement.style.setProperty('--promo-height', `${promoH}px`);
        document.documentElement.style.setProperty('--stack-height', `${promoH + 80}px`);
        document.documentElement.style.setProperty('--main-offset', `${promoH + 80}px`);
      } catch (e) {
        console.warn('updateHeights error:', e);
      }
    };
    updateHeights();
    let observer = null;
    try {
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(updateHeights);
        const promoEl = document.querySelector('.promo-banner, .announcement-bar');
        if (promoEl) observer.observe(promoEl);
      }
    } catch (e) {
      console.warn('ResizeObserver error:', e);
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="app-wrapper">
      <ScrollToTop />

      {/* Promo Banner - independently fixed at very top */}
      {!isAdminRoute && <PromoBanner />}

      {/* Header - fixed below promo banner via --promo-height CSS variable */}
      {!isAdminRoute && (
        <Header 
          theme={theme}
          setTheme={setTheme}
          onNavigate={handleNavigate}
          currentPath={location.pathname}
        />
      )}
      
      <main style={{ paddingTop: isAdminRoute ? '0' : 'var(--main-offset, 120px)' }}>
        <Routes>
          <Route path="/" element={<HomePage onNavigate={handleNavigate} articles={articles} faqs={faqs} />} />
          <Route path="/home" element={<HomePage onNavigate={handleNavigate} articles={articles} faqs={faqs} />} />
          <Route path="/bilateral-mastery" element={<BilateralMasteryPage onNavigate={handleNavigate} />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage articles={articles} onNavigate={handleNavigate} />} />
          <Route path="/blog/:id" element={<BlogPostPageWrapper articles={articles} onNavigate={handleNavigate} />} />
          <Route path="/faq" element={<FAQPage faqs={faqs} />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin" element={
            <React.Suspense fallback={<div style={{ padding: '4rem', textAlign: 'center', color: '#fff', fontSize: '1.2rem' }}>Loading Admin Console...</div>}>
              <AdminConsole articles={articles} setArticles={setArticles} faqs={faqs} setFaqs={setFaqs} onNavigate={handleNavigate} theme={theme} setTheme={setTheme} />
            </React.Suspense>
          } />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      {/* Footer - Hide on Admin */}
      {!isAdminRoute && <Footer onNavigate={handleNavigate} />}

      {/* Floating overlays */}
      {!isAdminRoute && <FloatingBuyButton />}
      <WhatsAppButton />
      <ChatWidget />
      {!isAdminRoute && <GoogleStoreFloatingBadge onOpenModal={() => setIsGoogleModalOpen(true)} />}
      {!isAdminRoute && <GoogleMerchantModal isOpen={isGoogleModalOpen} onClose={() => setIsGoogleModalOpen(false)} />}
      {/* Cookie consent — shown to all non-admin visitors */}
      {!isAdminRoute && <CookieBanner />}


    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
